import { FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useResetPassword } from '../lib/hooks';
import { Button, Field, InlineAlert } from '../components/ui';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const resetMutation = useResetPassword();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!token) {
      setError(
        'This reset link is incomplete. Please open the link from your email again, or request a new one from the sign-in page.',
      );
      return;
    }

    if (password !== confirmPassword) {
      setError('The two passwords do not match. Please type them again.');
      return;
    }

    try {
      await resetMutation.mutateAsync({
        token,
        password,
      });
      setMessage('Your password has been updated. Taking you to the sign-in page…');
      setTimeout(() => navigate('/login', { replace: true }), 1400);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-standalone" onSubmit={onSubmit}>
        <h2>Choose a new password</h2>
        <p className="auth-subtitle">
          Pick something memorable that you don&rsquo;t use anywhere else.
        </p>

        <Field label="New password" hint="At least 12 characters. A short sentence works great.">
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Minimum 12 characters"
            required
          />
        </Field>

        <Field label="Confirm new password">
          <input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            placeholder="Type it once more"
            required
          />
        </Field>

        {error && <InlineAlert variant="danger">{error}</InlineAlert>}
        {message && <InlineAlert variant="success">{message}</InlineAlert>}

        <Button type="submit" loading={resetMutation.isPending} loadingText="Updating password…">
          Update password
        </Button>

        <Link to="/login" style={{ justifySelf: 'center', fontSize: '0.86rem' }}>
          Back to sign in
        </Link>
      </form>
    </div>
  );
}
